import {
  Array,
  Boolean,
  Dictionary,
  Literal,
  Null,
  Number,
  Optional,
  Record as RRecord,
  Static,
  String,
  Union,
  Unknown,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums";

// Model definitions
export const SnowflakeCortexModelLiteral = Union(
  Literal("SNOWFLAKE_CORTEX_CLAUDE_3_5_SONNET"),
  Literal("SNOWFLAKE_CORTEX_CLAUDE_4_SONNET"),
);
export type SnowflakeCortexModel = Static<typeof SnowflakeCortexModelLiteral>;
export const SnowflakeCortexModel = getNormalEnum(SnowflakeCortexModelLiteral);

export const SnowflakeCortexModelNameMap: Record<SnowflakeCortexModel, string> =
  {
    [SnowflakeCortexModel.SNOWFLAKE_CORTEX_CLAUDE_3_5_SONNET]:
      "claude-3-5-sonnet",
    [SnowflakeCortexModel.SNOWFLAKE_CORTEX_CLAUDE_4_SONNET]: "claude-4-sonnet",
  };

export const SnowflakeCortexMessageRoleLiteral = Union(
  Literal("user"),
  Literal("assistant"),
  Literal("system"),
);
export type SnowflakeCortexMessageRole = Static<
  typeof SnowflakeCortexMessageRoleLiteral
>;
export const SnowflakeCortexMessageRole = getNormalEnum(
  SnowflakeCortexMessageRoleLiteral,
);

export const SnowflakeCortexToolUseBlock = RRecord({
  type: Literal("tool_use"),
  tool_use: RRecord({
    tool_use_id: String,
    name: String,
    input: Dictionary(Unknown, String),
  }),
});
export type SnowflakeCortexToolUseBlock = Static<
  typeof SnowflakeCortexToolUseBlock
>;

export const SnowflakeCortexTextBlock = RRecord({
  type: Literal("text"),
  text: String,
});
export type SnowflakeCortexTextBlock = Static<typeof SnowflakeCortexTextBlock>;
export const SnowflakeCortexToolResultBlock = RRecord({
  type: Literal("tool_results"),
  tool_results: RRecord({
    tool_use_id: String,
    name: String,
    content: Array(SnowflakeCortexTextBlock),
  }),
});
export type SnowflakeCortexToolResultBlock = Static<
  typeof SnowflakeCortexToolResultBlock
>;

export const SnowflakeCortexMessage = Union(
  RRecord({
    role: Literal(SnowflakeCortexMessageRole.system),
    content: String,
  }),
  RRecord({
    role: Literal(SnowflakeCortexMessageRole.user),
    content: String,
    content_list: Optional(
      Array(Union(SnowflakeCortexToolUseBlock, SnowflakeCortexToolResultBlock)),
    ),
  }),
  RRecord({
    role: Literal(SnowflakeCortexMessageRole.assistant),
    content: String,
    content_list: Optional(
      Array(Union(SnowflakeCortexToolUseBlock, SnowflakeCortexToolResultBlock)),
    ),
  }),
);
export type SnowflakeCortexMessage = Static<typeof SnowflakeCortexMessage>;

export const SnowflakeCortexToolType = Literal("generic");

export const SnowflakeCortexToolSpec = RRecord({
  type: String, // The type of tool. A combination of type and name is a unique identifier.
  name: String, // The name of the tool. A combination of type and name is a unique identifier.
  description: String, // A description of the tool being considered for tool use.
  input_schema: RRecord({
    type: SnowflakeCortexToolType, // The type of the input schema object. We only use `generic`
    properties: Dictionary(Unknown), // The definitions for each input parameter.
    required: Optional(Array(String)), // Array of required properties.
  }),
});
export type SnowflakeCortexToolSpec = Static<typeof SnowflakeCortexToolSpec>;

export const SnowflakeCortexToolResponse = RRecord({
  tool_use_id: Optional(String),
  name: Optional(String),
  input: Optional(Dictionary(Unknown, String)),
});
export type SnowflakeCortexToolResponse = Static<
  typeof SnowflakeCortexToolResponse
>;

export const SnowflakeCortexModelParams = RRecord({
  model: SnowflakeCortexModelLiteral,
  maxTokens: Number,
  temperature: Number.optional(),
  topP: Number.optional(),
  guardrails: Boolean.optional(),
});
export type SnowflakeCortexModelParams = Static<
  typeof SnowflakeCortexModelParams
>;

// Response types
// Define the types for items in the content_list
export const ContentTextBlock = RRecord({
  text: String,
  type: Literal("text"),
});
export type ContentTextBlock = Static<typeof ContentTextBlock>;

export const ToolContentBlock = RRecord({
  name: String,
  tool_use_id: String,
});
export type ToolContentBlock = Static<typeof ToolContentBlock>;

export const ContentInputBlock = RRecord({
  input: String,
});
export type ContentInputBlock = Static<typeof ContentInputBlock>;
// The content_list can contain any of these three kinds of blocks.
export const ContentBlock = Union(
  ContentTextBlock,
  ToolContentBlock,
  ContentInputBlock,
);
export type ContentBlock = Static<typeof ContentBlock>;
// The delta object may have an optional 'content' string and/or a 'content_list'
const Delta = RRecord({
  content: Optional(String),
  content_list: Optional(Union(Array(ContentBlock), ContentBlock)),
  // Tool-related fields
  type: Optional(String),
  name: Optional(String),
  tool_use_id: Optional(String),
  input: Optional(Union(String, Dictionary(Unknown, String))),
});
export type Delta = Static<typeof Delta>;
// Each choice contains a delta.
const Choice = RRecord({
  delta: Delta,
  finish_reason: Optional(String),
});

// The usage object may be empty or have token counts.
const Usage = RRecord({
  prompt_tokens: Optional(Number),
  completion_tokens: Optional(Number),
  total_tokens: Optional(Number),
});

// Top-level JSON object.
export const ResponseData = RRecord({
  id: String,
  model: String,
  choices: Array(Choice),
  usage: Usage,
});
export type ResponseData = Static<typeof ResponseData>;

// Array of top-level JSON objects.
export const SnowflakeCortexResponse = Array(ResponseData);
export type SnowflakeCortexResponse = Static<typeof SnowflakeCortexResponse>;

// Conformer result types
export const SnowflakeCortexConformerResult = RRecord({
  messages: Array(SnowflakeCortexMessage),
  totalContextTokens: Number.Or(Null),
  usedTokenCountApproximation: Boolean,
});
export type SnowflakeCortexConformerResult = Static<
  typeof SnowflakeCortexConformerResult
>;

// Error types
export const SnowflakeCortexErrorPayload = RRecord({
  type: Literal("error"),
  error: RRecord({
    type: String,
    message: String,
  }),
});
export type SnowflakeCortexErrorPayload = Static<
  typeof SnowflakeCortexErrorPayload
>;
